"use client";

import * as React from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ASSET_PREFIX } from "../../../config";

export type AccordionItem = {
  id: string | number;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
};

export type AccordionProps = {
  items: AccordionItem[];
  single?: boolean;
  defaultOpenIds?: Array<AccordionItem["id"]>;
  className?: string;
  itemClassName?: string;
};

export const Accordion: React.FC<AccordionProps> = ({
  items,
  single = false,
  defaultOpenIds = [],
  className = "",
  itemClassName = "",
}) => {
  const [open, setOpen] = React.useState<Set<AccordionItem["id"]>>(
    () => new Set(defaultOpenIds)
  );

  const toggle = (id: AccordionItem["id"], disabled?: boolean) => {
    if (disabled) return;
    setOpen((prev) => {
      const next = new Set(prev);
      const isOpen = next.has(id);
      if (single) {
        next.clear();
        if (!isOpen) next.add(id);
      } else {
        isOpen ? next.delete(id) : next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={
        "w-full divide-y divide-[#b08d38]  border-b border-[#b08d38] bg-white  " +
        className
      }>
      {items.map(({ id, title, content, disabled }, idx) => {
        const isOpen = open.has(id);
        const panelId = `accordion-panel-${id}`;
        const headingId = `accordion-heading-${id}`;
        return (
          <div
            key={id}
            className={
              "group/acc pr-4 sm:pr-6 " +
              (idx === 0 ? "rounded-t-2xl " : "") +
              (idx === items.length - 1 ? "rounded-b-2xl " : "") +
              itemClassName
            }>
            <button
              id={headingId}
              className={
                "flex w-full items-center justify-between gap-4 py-4 text-left outline-none transition-opacity cursor-pointer " +
                (disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:opacity-90")
              }
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(id, disabled)}
              disabled={disabled}>
              <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900">
                {title}
              </h2>
              <div className="p-[10px] rounded-[50%] bg-[#b1b1b1] w-[40px] h-[40px] flex items-center justify-center transition-transform duration-300  ">
                <ChevronDown
                  className={
                    "h-6 w-6 shrink-0 text-white  transition-transform duration-300 text-[#000]" +
                    (isOpen ? "rotate-180" : "rotate-0")
                  }
                  aria-hidden="true"
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.section
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={headingId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
                  style={{ overflow: "hidden" }}>
                  <div className="pb-5 text-[16px] leading-[22px] sm:text-base leading-relaxed w-[100%] md:w-[90%]  text-gray-700">
                    {content}
                  </div>
                </motion.section>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export const AccordionDemo: React.FC = () => {
  const items: AccordionItem[] = [
    {
      id: 1,
      title: "Brookefield Mall",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            The Brookfield Mall is a place where one can get something much deeper than ordinary; they have some of the best five-star malls. Located in Ram Nagar, the mall boasts multi-item layout stores, a multiplex cinema, and a fantastic food court. The mall features a children's play area and offers glamorous options for fashionistas, showcasing both international and Indian labels. His perfect blend of entertainment and shopping makes this place a really happening hub for the weekend crowd, just off TVS Nagar.
          </p>

        </>
      ),
    },

    {
      id: 2,
      title: "Pazha Market, Covai",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            If there is one place where you can savor realistic Coimbatore, then it would be Pazha Market, Covai. The freshest produce and best provisions are the hallmark of this market, which is favored by residents who are ever willing to pay for quality yet inexpensive goods. Loud and crowded with hundreds of stalls, the market is a great place to shop after work.
          </p>

        </>
      ),
    },

    {
      id: 3,
      title: "Onion Shop (Uzhavar Sandhai)",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            This farmer-managed market is one of the best places for those who believe in fresh fruits and vegetables. Onion Shops (Uzhavar Sandhai) sell fresh produce directly from the farmers themselves to the buyers, ensuring quality and a competitive price. This is a sustainable method of shopping and supports local agriculture while giving customers the best of seasonal harvests.
          </p>

        </>
      ),
    },

    {
      id: 4,
      title: "Sunday Market",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            A Sunday market in Ram Nagar is a bargain hunter's paradise. Lots of household goods, clothes, and accessories are sold at this market, ornamented with the vibrant colors and the jubilant atmosphere created by the crowd, which adds lively undertones to the shopping experiences of those who enjoy exploring various options.
          </p>

        </>
      ),
    },

    {
      id: 5,
      title: "Shopping in TVS Nagar",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            For shopping in Coimbatore, Diwan Bahadur Road is an apt place for an intriguing combination of the modern with the traditional. Shops open 24 hours, catering to a host of demands ranging from fashion and lifestyle to local specialties. Near the TVS Nagar area, this place is convenient, accessible, and a safe bet for the shoppers.
          </p>

        </>
      ),
    },

    {
      id: 6,
      title: "Farmer's Market, CB Road, RS Puram",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            This market is well known for its organic produce and fresh groceries. It has an overwhelming thousand-plus reviews, making it a worthy neighborhood place for both quality and range. Early mornings see some hustle and bustle with vendors selling vegetables, fruits, and other essentials. An apt place for health-conscious shoppers who vouch for natural, farm-fresh products.
          </p>

        </>
      ),
    },

    {
      id: 7,
      title: "Flower Market/Poovu Market",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            For plant lovers awash in colors and fragrances, Flower Market is quite exhilarating, as its shops cater to the numerous fresh flowers needed for weddings, festivals, and daily puja needs. The lively stalls offer perspectives on the culture of Coimbatore, making it worth visiting from near TVS Nagar.
          </p>

        </>
      ),
    },

    {
      id: 8,
      title: "Managartchi Valaikai Market",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Thadagam Main Road is indeed a wonderful example of a market that serves as a community shopping center. The market sells everything needed for day-to-day life and local goods, and although it is smaller than other markets, it is prized for its accessibility and neighborhood feel. People love it because the marketplace is where they go to make quick purchases nearby.
          </p>

        </>
      ),
    },


  ];

  return (
    <div className="">
      <Accordion items={items} single defaultOpenIds={[1]} />
    </div>
  );
};


export const AccordionDemo2: React.FC = () => {
  const items: AccordionItem[] = [
    {
      id: 1,
      title: "Ease in everyday shopping",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Local markets now feature easy purchases for fresh vegetables, groceries, and essentials at amazing prices.
          </p>

        </>
      ),
    },

    {
      id: 2,
      title: "The festive aura",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            For every celebration, the flower bazaars and traditional stalls add color and culture.
          </p>

        </>
      ),
    },

    {
      id: 3,
      title: "Modern Indulgence",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Premium malls like Brookefields offer all-in-one fashion, dining, and entertainment under one roof.
          </p>

        </>
      ),
    },

    {
      id: 4,
      title: "Community Spirit",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Farmers' markets and neighborhood shops engage local community members with their local producers and artisans.
          </p>

        </>
      ),
    },

    {
      id: 5,
      title: "Shopping around TVS is more than just a transaction",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            It emphasizes convenience, culture, and connection, especially in the context of festive and social shopping. Be it stocking up on the daily grocery needs, using the space for festive decorations, or just spending the weekend indulging in some retail therapy, here is your perfect blend of tradition and modernity.
          </p>

        </>
      ),
    },

    {
      id: 6,
      title: "Modern",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Enjoy world-class brands and entertainment in renowned malls.
          </p>

        </>
      ),
    },




  ];

  return (
    <div className="">
      <Accordion items={items} single defaultOpenIds={[1]} />
    </div>
  );
};


export default function MarketsShoppingPlaces() {
  const data = [
    {
      image: `/images/blogs/market-shopping-places-near-tvs-nagar.webp`,
      title: "Markets & Shopping Places near TVS Nagar",
      date: "1 Nov 2025",

      content1: (
        <>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            Coimbatore, November 2025 - Real estate experts say that TVS Nagar is a booming place for living, with all amenities, and the area is spotlighted for markets and shopping places around TVS Nagar.
          </p>

        </>
      ),



    },
  ];

  const data2 = [
    {

      content2: (
        <>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">Shopping Around TVS Nagar-A Lifestyle Experience</p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">TVS Nagar is more than just a place of residence; it is a gateway to some of the terrific shopping experiences one can have anywhere in Coimbatore. Street markets with hustle. The area is bustling with activity, featuring local produce and crafts, as well as modern malls that house international brands. Shopping becomes an exciting experience for everyone.</p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">Every street market is indeed one complex avant-garde market -- modern, colorful malls with international designers turn shopping into that one exciting experience for anyone on Earth.</p>

        </>
      ),

    },
  ];

  return (
    <div className="sticky top-[130px] z-[2] ">
      {data.map((item, index) => (
        <div className="group relative" key={index}>
          <h1 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] tracking-[-0.03em] font-[500] text-[var(--secondary)]">
            {item.title}
          </h1>
          <p className="text-[#924000] my-[20px] mb-[50px] max-mlg:mb-[30px] text-[20px] leading-[26px]  max-xl:my-[15px] ">
            {item.date}
          </p>

          <div className="rounded-[10px] overflow-hidden">
            <Image
              src={`${ASSET_PREFIX}${item.image}`}
              width={530}
              height={500}
              alt="Blog list"
              unoptimized
              className="w-full max-h-[500px] object-cover group-hover:scale-[1.1] transition duration-300 ease-linear"
            />
          </div>

          <div className="mt-[50px] ">
            <p className="my-[20px] text-[20px] leading-[26px] sm:text-[22px] sm:leading-[28px] font-[500] group-hover:text-[#8e722d] transition-colors duration-300  max-xl:my-[15px]">
              {item.title}
            </p>

            <div className="text-[16px] leading-[24px] mb-[20px] w-full font-light">
              {item.content1}
            </div>
          </div>

          <div className="mb-[20px]">
            <AccordionDemo />
          </div>



        </div>
      ))}


      {data2.map((item, index) => (
        <>
          <div className="mt-[50px] ">
            <h2 className="my-[20px] text-[20px] leading-[26px] sm:text-[22px] sm:leading-[28px] font-[500] group-hover:text-[#8e722d] transition-colors duration-300  max-xl:my-[15px]">
              Why Choose TVS Nagar for Shopping?
            </h2>

            <div className="text-[16px] leading-[24px] mb-[20px] w-full font-light">
              {item.content2}
            </div>
          </div>

          <div className="mb-[20px]">
            <AccordionDemo2 />
          </div>



          <div className="p-[30px] bg-[#e8e8e8] rounded-[30px] mt-[50px]">
            <h2 className="text-[22px] leading-[30px] md:text-[24px] md:leading-[32px] 2xl:text-[26px] 2xl:leading-[34px] font-medium text-gray-900 mb-[15px]">Conclusion</h2>
            <p className="mb-[15px]">In conclusion, shopping around TVS Nagar thus really does offer you both modern malls fitted with world-class amenities and traditional markets with local charm. Be it for fashion, food, or fastidious festival supplies, these destinations also make TVS Nagar lively for every kind of shopper.</p>
          </div>
        </>
      ))}
    </div>
  );
}
